import { useState } from 'react';

import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useDecide } from '../api/approvals.queries';
import { kindLabel } from '../lib/approval-kinds';
import type { InboxApproval } from '../types/approval.types';

const REASON_CLASS =
  'border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent p-2 text-sm outline-none focus-visible:ring-[3px]';

export type Decision = 'approve' | 'reject' | 'cancel';

const TITLE: Record<Decision, string> = {
  approve: 'Approve this request?',
  reject: 'Reject this request?',
  cancel: 'Withdraw your request?',
};

const CONFIRM: Record<Decision, string> = {
  approve: 'Approve',
  reject: 'Reject',
  cancel: 'Withdraw',
};

/**
 * Confirms a decision on a request, through the type's own endpoint (which
 * enforces the rules). A rejection needs a reason, shown to the requester; an
 * approval may carry one; withdrawing needs none. Errors are toasted by
 * useDecide. Mount only while open.
 */
export function DecisionDialog({
  approval,
  decision,
  onClose,
}: {
  approval: InboxApproval;
  decision: Decision;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const decide = useDecide();
  const rejecting = decision === 'reject';

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={TITLE[decision]}
      description={
        decision === 'cancel'
          ? `${kindLabel(approval.type)}: ${approval.summary}. Nothing changes, and you can ask again.`
          : `${kindLabel(approval.type)}: ${approval.summary}.`
      }
      confirmLabel={CONFIRM[decision]}
      variant={rejecting ? 'destructive' : 'default'}
      isPending={decide.isPending}
      confirmDisabled={rejecting && reason.trim().length < 3}
      onConfirm={() => {
        const trimmed = reason.trim();
        decide.mutate(
          {
            type: approval.type,
            approvalId: approval.id,
            decision,
            reason: trimmed === '' ? null : trimmed,
          },
          { onSuccess: onClose },
        );
      }}
    >
      {decision !== 'cancel' && (
        <div className="space-y-1.5">
          <Label htmlFor="decision-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              {rejecting ? '(required, shown to the requester)' : '(optional)'}
            </span>
          </Label>
          <textarea
            id="decision-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            maxLength={500}
            className={REASON_CLASS}
          />
        </div>
      )}
    </ConfirmDialog>
  );
}
